import { CalendarDots, Camera, GlobeHemisphereWest, MapPin, UsersThree, X } from '@phosphor-icons/react';
import { useRef, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  POST_MAX_LENGTH,
  type FeedPage,
  type Post,
  type PostMention,
  type PublicUser,
  type SiroccoEvent,
} from '@sirocco/shared';
import { events as eventsApi, posts as postsApi, users as usersApi } from '../../api/endpoints';
import { ScreenLoader } from '../../components/layout/Screens';
import { Button } from '../../components/ui/Button';
import { Avatar } from '../../components/ui/Avatar';
import { EmptyState } from '../../components/ui/Bits';
import { PhotoPicker } from '../../components/ui/PhotoPicker';
import { Sheet, SheetConfirm } from '../../components/ui/Sheet';
import { updateScreenCache, useAsync } from '../../hooks/useAsync';
import { useSession } from '../../store/SessionContext';
import { shortDate, timeRange } from '../../utils/format';
import { insertMention, typingMention } from '../../utils/mentions';
import { t } from '../../i18n';

/**
 * Ecriture d'un post.
 *
 * Ecran plein, sans barre d'onglets : on entre, on ecrit, on publie.
 * Le compteur de caracteres passe au rouge des que la limite est
 * franchie, et le bouton Publier se desactive.
 *
 * Trois pieces jointes possibles, une photo, un lieu et un evenement, et
 * chacune s'accompagne du texte qu'on veut : joindre sa sortie sans
 * pouvoir ecrire "venez" n'aurait pas d'interet.
 */
export function ComposeScreen() {
  const { user, server } = useSession();
  const navigate = useNavigate();
  /*
    Ecrire dans le fil d'une association, depuis sa page : le post y
    parait, et dans le fil de l'ecole avec son nom. La fonction qui publie
    verifie qu'on en est membre.
  */
  const [parametres] = useSearchParams();
  const assoDemandee = parametres.get('asso');
  const asso = assoDemandee && server?.associations?.[assoDemandee] ? assoDemandee : null;
  const nomDeLAsso = asso ? server?.associations?.[asso]?.nom : undefined;

  const [content, setContent] = useState('');
  const [photo, setPhoto] = useState<string | null>(null);
  const [place, setPlace] = useState<string | null>(null);
  const [event, setEvent] = useState<SiroccoEvent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  /** Quel selecteur est ouvert : aucun, le lieu, ou l'evenement. */
  const [picker, setPicker] = useState<'place' | 'event' | null>(null);
  const [placeDraft, setPlaceDraft] = useState('');

  /*
    Les membres cites.

    On garde tous ceux qu'on a choisis, meme si leur pseudo est ensuite
    efface : le tri se fait a la publication (`keepCited`), sur le texte
    definitif. Retirer au fil de la frappe ferait perdre une mention des
    qu'on corrige une lettre au milieu.
  */
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [mentions, setMentions] = useState<PostMention[]>([]);
  const [typed, setTyped] = useState<string | null>(null);

  /*
    Les suggestions : au moins une lettre apres le « @ ».

    Sur le seul « @ », la liste entiere des membres du spot n'aiderait
    personne et couterait une lecture complete a chaque frappe.
  */
  const { data: matches } = useAsync<PublicUser[]>(
    () => (typed && typed.length >= 1 ? usersApi.search(typed) : Promise.resolve([])),
    [typed],
  );

  /** Le curseur bouge : on regarde si un pseudo est en cours de frappe. */
  function onCaretMoved() {
    const input = inputRef.current;
    if (!input) return;
    setTyped(typingMention(input.value, input.selectionStart ?? input.value.length));
  }

  function pickMention(member: PublicUser) {
    const input = inputRef.current;
    if (!input) return;

    const next = insertMention(content, input.selectionStart ?? content.length, member.username);
    setContent(next.content);
    setMentions((current) =>
      current.some((mention) => mention.id === member.id)
        ? current
        : [...current, { id: member.id, username: member.username }],
    );
    setTyped(null);

    /*
      Le curseur se repose apres l'insertion, une fois le rendu passe :
      React reecrit la valeur du champ et remettrait le curseur a la fin.
      Le champ garde le doigt, sinon le clavier se refermerait.
    */
    requestAnimationFrame(() => {
      input.focus();
      input.setSelectionRange(next.caret, next.caret);
    });
  }

  const tooLong = content.length > POST_MAX_LENGTH;
  // Une photo ou un evenement se suffit a lui-meme : le texte devient
  // facultatif. Un lieu seul, non : c'est une precision, pas un post.
  const canPublish = (content.trim().length > 0 || photo !== null || event !== null) && !tooLong;

  async function onPublish() {
    if (!canPublish) return;
    setError(null);
    setPending(true);
    try {
      const poste = await postsApi.create(
        content.trim(),
        photo,
        place,
        event?.id ?? null,
        mentions,
        asso,
      );
      /*
        Le post rejoint le fil avant meme qu'on y revienne.

        L'accueil ne se relit pas au retour — c'est voulu, sans quoi le
        post qu'on lisait glisserait sous ceux arrives entre-temps. Mais
        le sien ne s'affichait alors qu'apres un tirage, ce qui donnait
        l'impression que la publication avait echoue. Seule l'entree sans
        recherche est touchee : un resultat de recherche n'a pas a
        accueillir un post qui n'y repond peut-etre pas.
      */
      updateScreenCache<FeedPage>('feed', (page) => ({
        ...page,
        posts: [poste, ...page.posts],
      }));
      /* Depuis une association, on revient a son fil, ou le post attend deja. */
      if (asso) {
        updateScreenCache<Post[]>(`association-fil-${server?.id}-${asso}`, (liste) => [poste, ...liste]);
        navigate(-1);
        return;
      }
      navigate('/app/feed', { replace: true });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t.fil.composer.impossible);
    } finally {
      setPending(false);
    }
  }

  function openPlacePicker() {
    setPlaceDraft(place ?? '');
    setPicker('place');
  }

  function savePlace() {
    const trimmed = placeDraft.trim();
    setPlace(trimmed.length > 0 ? trimmed : null);
    setPicker(null);
  }

  return (
    <div className="compose">
      <header className="compose__bar">
        <button className="compose__cancel" onClick={() => navigate(-1)}>
          {t.commun.annuler}
        </button>
        <h1 className="compose__title">{t.fil.composer.titre}</h1>
        <Button
          variant="primary"
          size="sm"
          disabled={!canPublish}
          loading={pending}
          onClick={onPublish}
        >
          {t.fil.composer.publier}
        </Button>
      </header>

      <div className="compose__who">
        <Avatar user={user} size={38} />
        <span className="compose__spot">
          {nomDeLAsso ? <UsersThree size={16} weight="fill" /> : <MapPin size={16} weight="fill" />}
          {nomDeLAsso ? t.messages.annuaire.associations.dansLeFil(nomDeLAsso) : server?.name}
        </span>
      </div>

      <textarea
        ref={inputRef}
        className="compose__input"
        placeholder={t.fil.composer.quoiDeNeuf(server?.name ?? 'Sirocco')}
        value={content}
        onChange={(changed) => {
          setContent(changed.target.value);
          onCaretMoved();
        }}
        onKeyUp={onCaretMoved}
        onClick={onCaretMoved}
        autoFocus
      />

      {/*
        Les membres proposes pendant qu'on tape un « @ ».

        La liste se pose sous le champ plutot qu'au-dessus du clavier :
        l'ecran de redaction ne defile pas, et une liste flottante
        recouvrirait le texte qu'on est en train d'ecrire.
      */}
      {typed !== null && (matches?.length ?? 0) > 0 && (
        <ul className="compose__mentions">
          {matches!.slice(0, 5).map((member) => (
            <li key={member.id}>
              <button
                type="button"
                className="compose__mention"
                onClick={() => pickMention(member)}
              >
                <Avatar user={member} size={30} />
                <span className="compose__mention-name">{member.username}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* Les pieces jointes se relisent avant de publier, et se retirent. */}
      {place && (
        <Attachment
          label={place}
          icon={<MapPin size={16} weight="fill" />}
          onRemove={() => setPlace(null)}
        >
          {t.fil.composer.lieu}
        </Attachment>
      )}

      {event && (
        <Attachment
          label={`${event.title} · ${shortDate(event.date)} · ${timeRange(event.startTime, event.endTime)}`}
          icon={<CalendarDots size={16} weight="fill" />}
          onRemove={() => setEvent(null)}
        >
          {t.fil.composer.evenement}
        </Attachment>
      )}

      {photo && (
        <div className="compose__photo">
          <img src={photo} alt={t.fil.composer.photoJointe} />
          <button
            className="compose__photo-remove"
            aria-label={t.fil.composer.retirerPhoto}
            onClick={() => setPhoto(null)}
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      )}

      {error && <p className="compose__error">{error}</p>}

      <footer className="compose__foot">
        <div className="compose__meta">
          <span className={tooLong ? 'compose__count compose__count--over' : 'compose__count'}>
            {content.length} / {POST_MAX_LENGTH}
          </span>
          <span className="compose__scope">
            <GlobeHemisphereWest size={18} />
            {t.fil.composer.visible}
          </span>
        </div>

        <div className="compose__tools">
          <PhotoPicker
            preset="photo"
            label={photo ? t.fil.composer.remplacerPhoto : t.fil.composer.ajouterPhoto}
            className={`compose__tool${photo ? ' compose__tool--on' : ''}`}
            onPicked={setPhoto}
            onError={setError}
          >
            <Camera size={22} weight={photo ? 'fill' : 'regular'} />
          </PhotoPicker>

          <button
            className={`compose__tool${place ? ' compose__tool--on' : ''}`}
            aria-label={place ? t.fil.composer.lieuChoisi(place) : t.fil.composer.ajouterLieu}
            onClick={openPlacePicker}
          >
            <MapPin size={22} weight={place ? 'fill' : 'regular'} />
          </button>

          <button
            className={`compose__tool${event ? ' compose__tool--on' : ''}`}
            aria-label={event ? t.fil.composer.evenementChoisi(event.title) : t.fil.composer.joindreEvenement}
            onClick={() => setPicker('event')}
          >
            <CalendarDots size={22} weight={event ? 'fill' : 'regular'} />
          </button>
        </div>
      </footer>

      {/*
        Le lieu est libre : la base ne tient pas de repertoire d'adresses,
        et une liste figee vieillirait plus vite qu'un champ de saisie.
      */}
      <Sheet
        open={picker === 'place'}
        large
        onClose={() => setPicker(null)}
        title={t.fil.composer.ouDans(server?.name ?? '')}
      >
        {/*
          Pas de `autoFocus` ici.

          Le clavier montait pendant que la fenetre s'ouvrait : la zone
          visible changeait de hauteur au milieu de l'animation, et
          l'apparition sautait. La fenetre des filtres, elle, ne prend
          rien au clavier — c'est tout ce qui la rendait plus douce. Le
          champ est sous le pouce, une touche suffit.
        */}
        <input
          className="compose__place-input"
          maxLength={80}
          value={placeDraft}
          aria-label={t.fil.composer.lieu}
          onChange={(changed) => setPlaceDraft(changed.target.value)}
          onKeyDown={(pressed) => {
            if (pressed.key === 'Enter') savePlace();
          }}
        />
        {/* Le libelle dit ce que fait le bouton : ajouter, retirer, ou rien. */}
        <SheetConfirm
          label={placeDraft.trim() ? t.fil.composer.ajouterCeLieu : place ? t.fil.composer.retirerLieu : t.fil.composer.termine}
          onClick={savePlace}
        />
      </Sheet>

      <EventPicker
        open={picker === 'event'}
        onClose={() => setPicker(null)}
        onPick={(picked) => {
          setEvent(picked);
          setPicker(null);
        }}
      />
    </div>
  );
}

/** Une piece jointe relue avant publication : son intitule, et une croix. */
function Attachment({
  icon,
  label,
  children,
  onRemove,
}: {
  icon: ReactNode;
  label: string;
  /** Nature de la piece jointe : "Lieu", "Événement". */
  children: ReactNode;
  onRemove: () => void;
}) {
  return (
    <div className="compose__attach">
      {icon}
      <span className="compose__attach__text">
        <span className="compose__attach__kind">{children}</span>
        <span className="compose__attach__label">{label}</span>
      </span>
      <button className="compose__attach__remove" aria-label={t.fil.composer.retirer} onClick={onRemove}>
        <X size={15} weight="bold" />
      </button>
    </div>
  );
}

/**
 * Choix d'un evenement du feed public.
 *
 * La liste n'est chargee qu'a l'ouverture de la feuille : ecrire un post
 * sans rien joindre est le cas courant, et il n'a pas a payer une lecture
 * de la base.
 */
function EventPicker({
  open,
  onClose,
  onPick,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (event: SiroccoEvent) => void;
}) {
  const { data, loading } = useAsync(() => (open ? eventsApi.list() : Promise.resolve([])), [open]);

  return (
    <Sheet open={open} large onClose={onClose} title={t.fil.composer.quelEvenement}>
      {loading && <ScreenLoader />}

      {data && data.length === 0 && !loading && (
        <EmptyState title={t.fil.composer.aucunEvenement} hint={t.fil.composer.aucunEvenementAide} />
      )}

      <div className="compose__events">
        {data?.map((event) => (
          <button key={event.id} className="compose__event" onClick={() => onPick(event)}>
            <span className="compose__event__title">{event.title}</span>
            <span className="compose__event__meta">
              {event.location} · {shortDate(event.date)} ·{' '}
              {timeRange(event.startTime, event.endTime)}
            </span>
          </button>
        ))}
      </div>
    </Sheet>
  );
}
